import React, { useState, useMemo } from 'react';
import { Search, CheckCircle, X, Calendar, Users, AlertTriangle, Scan } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { formatDate, getTodayString, daysUntilExpiry } from '../utils/helpers';
import ConfirmDialog from '../components/ui/ConfirmDialog';

const Attendance = () => {
  const navigate = useNavigate();
  const { members, attendances, addAttendance, deleteAttendance, memberships } = useApp();
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedDate, setSelectedDate] = useState(getTodayString());
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const today = getTodayString();

  const todayAttendances = attendances.filter(a => a.date === today);
  const selectedDateAttendances = attendances.filter(a => a.date === selectedDate);

  const handleSearch = (query) => {
    setSearch(query);
    if (!query.trim()) { setSearchResults([]); return; }
    const q = query.toLowerCase();
    const results = members.filter(m =>
      m.name?.toLowerCase().includes(q) || m.phone?.includes(q)
    ).slice(0, 5);
    setSearchResults(results);
  };

  const handleCheckIn = (member) => {
    addAttendance(member.id);
    setSearch('');
    setSearchResults([]);
  };

  const isCheckedToday = (memberId) => todayAttendances.some(a => a.memberId === memberId);

  // 회원의 활성 회원권 만료 정보
  const getMembershipWarning = (memberId) => {
    const active = memberships.filter(ms => ms.memberId === memberId && ms.status !== 'expired' && !ms.frozenAt);
    if (active.length === 0) return { type: 'none', days: null };
    const soonest = active.reduce((min, ms) => {
      const d = daysUntilExpiry(ms.endDate);
      if (d === null) return min;
      return min === null || d < min ? d : min;
    }, null);
    if (soonest === null || soonest < 0) return { type: 'expired', days: soonest };
    if (soonest <= 7) return { type: 'expiring', days: soonest };
    return { type: 'ok', days: soonest };
  };

  const displayAttendances = useMemo(() => {
    return [...selectedDateAttendances].sort((a, b) => b.time.localeCompare(a.time));
  }, [selectedDateAttendances]);

  return (
    <div className="space-y-6">
      {/* 출석 체크 */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            출석 체크
          </h2>
          <button
            onClick={() => navigate('/kiosk')}
            className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Scan className="w-4 h-4" />
            QR 키오스크 모드
          </button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="회원 이름 또는 전화번호 검색..."
            value={search}
            onChange={e => handleSearch(e.target.value)}
            className="input pl-9 text-base"
            autoComplete="off"
          />
          {search && (
            <button onClick={() => { setSearch(''); setSearchResults([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* 검색 결과 */}
        {searchResults.length > 0 && (
          <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            {searchResults.map(member => {
              const checked = isCheckedToday(member.id);
              const warning = getMembershipWarning(member.id);
              return (
                <div
                  key={member.id}
                  className={`flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700 last:border-0 transition-colors ${warning.type === 'expired' ? 'bg-red-50/50 dark:bg-red-900/10' : warning.type === 'expiring' ? 'bg-yellow-50/50 dark:bg-yellow-900/10' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                      <span className="text-sm font-semibold text-primary-700 dark:text-primary-400">{member.name[0]}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{member.name}</p>
                        {warning.type === 'expired' && <span className="flex items-center gap-0.5 text-xs text-red-500 font-medium"><AlertTriangle className="w-3 h-3" /> 만료</span>}
                        {warning.type === 'expiring' && <span className="flex items-center gap-0.5 text-xs text-yellow-500 font-medium"><AlertTriangle className="w-3 h-3" /> D-{warning.days}</span>}
                        {warning.type === 'none' && <span className="text-xs text-gray-400">회원권 없음</span>}
                      </div>
                      <p className="text-xs text-gray-400">{member.phone}</p>
                    </div>
                  </div>
                  {checked ? (
                    <span className="badge-green flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> 출석 완료
                    </span>
                  ) : (
                    <button
                      onClick={() => handleCheckIn(member)}
                      className="btn-primary text-xs px-3 py-1.5"
                    >
                      출석 체크
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {search && searchResults.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-4">검색 결과가 없습니다.</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 오늘 출석 현황 */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              오늘 출석 현황
            </h2>
            <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">{todayAttendances.length}명</span>
          </div>
          {todayAttendances.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">오늘 출석한 회원이 없습니다.</p>
          ) : (
            <div className="space-y-1">
              {[...todayAttendances].sort((a, b) => b.time.localeCompare(a.time)).map(att => {
                const member = members.find(m => m.id === att.memberId);
                if (!member) return null;
                return (
                  <div key={att.id} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 group transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{member.name}</p>
                        <p className="text-xs text-gray-400">{att.time}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setDeleteConfirm(att)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all rounded"
                      title="출석 취소"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 날짜별 출석 조회 */}
        <div className="card p-5">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-500" />
            날짜별 출석 조회
          </h2>
          <input
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            className="input mb-4"
          />
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-gray-500 dark:text-gray-400">{formatDate(selectedDate)}</p>
            <span className="font-semibold text-gray-900 dark:text-white">{displayAttendances.length}명</span>
          </div>
          {displayAttendances.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">출석 기록이 없습니다.</p>
          ) : (
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {displayAttendances.map(att => {
                const member = members.find(m => m.id === att.memberId);
                if (!member) return null;
                return (
                  <div key={att.id} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                        <span className="text-xs font-semibold text-primary-700 dark:text-primary-400">{member.name[0]}</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{member.name}</span>
                    </div>
                    <span className="text-sm text-gray-400">{att.time}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!deleteConfirm}
        title="출석 취소"
        message="이 출석 기록을 삭제하시겠습니까?"
        onConfirm={() => { deleteAttendance(deleteConfirm.id); setDeleteConfirm(null); }}
        onCancel={() => setDeleteConfirm(null)}
        confirmText="취소"
      />
    </div>
  );
};

export default Attendance;
